import React from 'react';
import { forwardRef } from '@shared/utils/forwardRef';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ReactComponent as UnionIcon } from '@assets/icons/union-quick-add.svg';
import {
  ProductTitleText,
  quickAddButtonOverrides,
  StyledFlex,
  StyledIcon,
  StyledProductPhoto,
  StyledProductTile,
  StyledQuickAddButton,
  styles,
} from './BaseProductDetails.styles';
import { addRendition } from '@shared/utils/photoRendition';
import type { BaseProductDetailsProps } from './BaseProductDetails.types';
import { SwatchColorGrid } from '../../../../../SwatchColorGrid';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { ProductStorefrontStatusBadge } from '../../../ProductDetails/components/ProductStorefrontStatusBadge';
import { ProductCatalogItemType, ProductCatalogStorefrontStatus } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import type { SwatchColorOption } from '@apps/registry/common/components/SwatchColorGrid/types';
import type { StarRatingShimProps } from '../../../ProductDetails/components/ProductRating';
import { StarRatingWithLabel, StarRatingShim } from '../../../ProductDetails/components/ProductRating';
import { customRounding } from '@shared/utils/numbers';
import { CatalogProductItemTags } from '../../../../Catalog.types';
import { OutOfStockBadge } from '../../../OutOfStockBadge';
import { useIsShopOutOfStockBadgeEnabled } from '../../../../hooks/useIsShopOutOfStockBadgeEnabled';

const LazyLoadingStyledProductPhoto = withLazyPropOnIntersection(StyledProductPhoto, 'photoUrl');

export const BaseProductDetails = React.memo(
  forwardRef<'div', BaseProductDetailsProps>((props, ref) => {
    const {
      onDetailClick,
      onDetailKeyPress,
      isAddLoading,
      productDisplayFields,
      isMobile,
      isHovered,
      isQuickAdd,
      isGroupItem,
      onViewMoreDimensionClick,
      onChangeActiveDimensionOption,
      onQuickAddClick,
      swatchColorOptions,
      preload,
      storefrontInfo,
      hasAnyStorefrontWarning,
      variant,
      initiallySelectedProduct,
      productRating,
      formatPrice,
      ...restProps
    } = props;

    const { id, brand, title, photoUrl, price, fullPrice, isFullPrice: isSingleFullPrice, tags } = productDisplayFields;

    const storefrontStatus = storefrontInfo?.storefrontStatus;

    const { t } = useTranslation('catalogRegistry');
    const { t: translations } = useTranslation('catalogRegistry');
    const { message: salesPriceMessage } = translations('salesPrice');
    const { priceRange } = t('productTile');
    const { joyExclusive, quickAddToRegistry } = t('productDetailPage');

    const isOutOfStockBadgeEnabled = useIsShopOutOfStockBadgeEnabled();
    const isOutOfStock = isOutOfStockBadgeEnabled && initiallySelectedProduct?.inventory?.maxQty === 0;
    const isCashFundType = initiallySelectedProduct?.itemType === ProductCatalogItemType.cash;
    const joyExclusiveLabel = tags.includes(CatalogProductItemTags.joyExclusive) ? joyExclusive() : null;
    const isGroupItemAndNotHovered = isGroupItem && !isHovered;
    const initialTilePresentation = initiallySelectedProduct?.tilePresentation;

    const displayTitle = isGroupItemAndNotHovered ? initialTilePresentation?.title : title;
    const displayPhotoUrl = isGroupItemAndNotHovered ? initialTilePresentation?.photos[0]?.url : photoUrl;
    const isPriceRange = isGroupItemAndNotHovered ? !initialTilePresentation?.fullPriceRange?.isSinglePrice : false;
    const displayPrice = isGroupItemAndNotHovered ? initialTilePresentation?.fullPriceRange?.lowerPrice : fullPrice;
    const salePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.lowerPrice : price;
    const displayUpperPrice = isGroupItemAndNotHovered ? initialTilePresentation?.fullPriceRange?.upperPrice : null;
    const upperSalePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.upperPrice : fullPrice;
    const lowerSalePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.lowerPrice : null;
    const isFullPriceRange = isGroupItemAndNotHovered ? initialTilePresentation?.isFullPrice : isSingleFullPrice;

    const formattedPrice = formatPrice(displayPrice);
    const formattedUpperPrice = formatPrice(displayUpperPrice);
    const formattedUpperSalePrice = formatPrice(upperSalePrice);
    const formattedLowerSalePrice = formatPrice(lowerSalePrice);
    const salePriceValue = formatPrice(salePrice);

    const onScreenPhotoUrl = addRendition({ url: displayPhotoUrl, renditionSize: 'medium', isProduct: true });
    const isGridItem = variant === 'gridItem';
    const hasDarkBackground = variant === 'spotlightCarouselItem' && !isHovered;
    const showColorSwatch = swatchColorOptions?.length > 1;

    const handleInteract = (e: React.SyntheticEvent, option: SwatchColorOption) => {
      if (isMobile && e.type === 'click') {
        onQuickAddClick?.(e as React.MouseEvent, option.id);
        return;
      }
      onChangeActiveDimensionOption(option);
    };

    // shared by the Shim and the "real thing"
    const starRatingProps: StarRatingShimProps = {
      visibility: productRating?.averageRating ? 'visible' : 'hidden',
      marginTop: 6,
      rating: customRounding(productRating?.averageRating) || 0,
      labelVariant: 'noLabel',
      showPartialRating: true,
      iconSize: 14,
    };

    return (
      <StyledProductTile
        tabIndex={0}
        id={id}
        data-id={id}
        onKeyPress={onDetailKeyPress}
        onClick={onDetailClick}
        ref={ref}
        {...restProps}
      >
        {hasAnyStorefrontWarning && (
          <Flex __css={styles.storefrontHeadContainer}>
            {storefrontStatus === ProductCatalogStorefrontStatus.comingSoon && (
              <ProductStorefrontStatusBadge
                storefrontStatus={storefrontStatus}
                variant="head"
                type="title"
              />
            )}
          </Flex>
        )}
        <StyledFlex
          flexDirection="column"
          __css={{ cursor: 'pointer ' }}
        >
          <Box
            marginBottom={'16px'}
            height={'1rem'}
            textAlign="center"
          >
            {joyExclusiveLabel && (
              <TextV2
                as="span"
                color="purple6"
                typographyVariant="hed2"
                fontSize={500}
              >
                {joyExclusiveLabel}
              </TextV2>
            )}
          </Box>

          <StyledFlex
            backgroundColor={'white'}
            justifyContent="center"
            alignItems="center"
            __css={styles.photoWrapper}
            width={isQuickAdd ? '70%' : '100%'}
            margin={isQuickAdd ? 'auto' : 'initial'}
            paddingTop={isQuickAdd ? '75%' : '100%'}
          >
            <LazyLoadingStyledProductPhoto
              photoUrl={{
                onscreen: onScreenPhotoUrl,
                offscreen: preload ? onScreenPhotoUrl : undefined,
              }}
            />
          </StyledFlex>
          {productRating ? (
            <StarRatingWithLabel
              id={id}
              {...starRatingProps}
              totalReviewCount={productRating.totalReviewCount}
              singleLine={true}
              justifyContent={isGridItem ? 'center' : 'flex-start'}
            />
          ) : (
            <StarRatingShim {...starRatingProps} />
          )}
          <Flex
            flexDirection="row"
            alignItems="flex-start"
            marginTop={5}
            justifyContent={isGridItem ? 'center' : 'space-between'}
            paddingX={[3, null, 0]}
          >
            <StyledFlex
              flexDirection="column"
              alignItems={isGridItem ? 'center' : 'flex-start'}
              __css={styles.flexTextWrapper}
            >
              {brand && (
                <TextV2
                  as="span"
                  typographyVariant="body1"
                  __css={hasDarkBackground ? styles.brandTextDarkBg : styles.brandText}
                >
                  {brand}
                </TextV2>
              )}
              {displayTitle && (
                <ProductTitleText
                  typographyVariant="hed2"
                  $variant={variant}
                  fontSize={500}
                  title={displayTitle}
                  __css={hasDarkBackground ? styles.productTitleDarkBg : styles.productTitle}
                >
                  {displayTitle}
                </ProductTitleText>
              )}

              {displayPrice && (
                <Flex
                  justifyContent={'center'}
                  flexDirection={(isPriceRange && 'column') || 'row'}
                  alignItems={(!isCashFundType && isPriceRange && 'center') || ''}
                  gap="2"
                >
                  <TextV2
                    as="span"
                    color={hasDarkBackground ? 'white' : 'mono10'}
                    textDecoration={!isFullPriceRange ? 'line-through' : ''}
                    typographyVariant="label6"
                    alignContent="center"
                  >
                    {isPriceRange
                      ? priceRange({
                          lowerBound: formattedPrice,
                          upperBound: formattedUpperPrice,
                        })
                      : formattedPrice}
                  </TextV2>
                  {!isFullPriceRange && (
                    <TextV2
                      as="span"
                      color="#D80B0B"
                      typographyVariant="label6"
                      alignContent="center"
                    >
                      {salesPriceMessage()}{' '}
                      {(!isPriceRange && salePriceValue) ||
                        priceRange({
                          lowerBound: formattedLowerSalePrice,
                          upperBound: formattedUpperSalePrice,
                        })}
                    </TextV2>
                  )}
                </Flex>
              )}
              {isOutOfStock && (
                <Box marginTop={2}>
                  <OutOfStockBadge />
                </Box>
              )}
            </StyledFlex>
            {!isGridItem && !isHovered && !isMobile && (
              <StyledFlex>
                <StyledIcon>
                  <UnionIcon />
                </StyledIcon>
              </StyledFlex>
            )}
          </Flex>
          {showColorSwatch && !isHovered && (
            <Flex
              justifyContent={isGridItem ? 'center' : 'flex-start'}
              marginTop={7}
              paddingX={[4, null, 0]}
              maxWidth="100%"
              __css={!isHovered ? {} : styles.hovered}
            >
              <SwatchColorGrid
                alignment={'center'}
                swatchSize={6}
                rows={1}
                columns={[4, null, 4]}
                options={swatchColorOptions}
                interactionType={'click'}
                onInteract={handleInteract}
                onViewMoreClick={onViewMoreDimensionClick}
                hasDarkBackground={hasDarkBackground}
              />
            </Flex>
          )}
          {isMobile && !isQuickAdd && (
            <Flex
              marginTop={2}
              marginBottom={6}
              justifyContent={isGridItem ? 'center' : 'flex-start'}
              paddingX={3}
            >
              <StyledQuickAddButton
                startIcon={UnionIcon}
                loading={isAddLoading}
                variant="link"
                overrides={quickAddButtonOverrides}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  onQuickAddClick?.(e);
                }}
                typographyVariant="button2"
                marginTop={2}
                disabled={!storefrontInfo?.canAddToRegistry}
                $darkBg={hasDarkBackground}
              >
                {quickAddToRegistry()}
              </StyledQuickAddButton>
            </Flex>
          )}
        </StyledFlex>
      </StyledProductTile>
    );
  }),
);
BaseProductDetails.displayName = 'BaseProductDetailsV2';
